/**
 * 任务管理：筛选（状态/优先级/领域/项目/标签/搜索）+ 列表 + 分页 + 详情抽屉 + 快速添加。
 */
import { useMemo, useState } from 'react';
import { QuickAdd } from '../components/QuickAdd';
import { TaskDrawer } from '../components/TaskDrawer';
import { Icon } from '../components/icons';
import {
  Button,
  Card,
  EmptyState,
  ErrorBox,
  LoadingBlock,
  Pagination,
  Select,
  Input,
  Tabs,
} from '../components/ui';
import { useAreas, useProjects, useTags, useTasks, useCompleteTask, useCancelTask } from '../hooks/useTasks';
import type { TasksQueryParams } from '../lib/types';
import {
  PRIORITY_LABELS,
  PRIORITY_STYLES,
  PRIORITY_OPTIONS,
  STATUS_LABELS,
  relativeDay,
  todayStr,
} from '../lib/utils';

type StatusTab = 'all' | 'todo' | 'doing' | 'done' | 'cancelled';

const STATUS_TABS: Array<{ key: StatusTab; label: string }> = [
  { key: 'all', label: '全部' },
  { key: 'todo', label: '待办' },
  { key: 'doing', label: '进行中' },
  { key: 'done', label: '已完成' },
  { key: 'cancelled', label: '已取消' },
];

export function TasksPage() {
  const [tab, setTab] = useState<StatusTab>('all');
  const [q, setQ] = useState('');
  const [priority, setPriority] = useState('');
  const [areaId, setAreaId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [tag, setTag] = useState('');
  const [sort, setSort] = useState<'due' | 'priority' | 'created' | 'updated'>('due');
  const [page, setPage] = useState(1);
  const [quickOpen, setQuickOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<string | null>(null);

  const { data: areas } = useAreas();
  const { data: projects } = useProjects({ pageSize: 100 });
  const { data: tags } = useTags();
  const completeTask = useCompleteTask();
  const cancelTask = useCancelTask();

  const query: TasksQueryParams = useMemo(() => {
    const base: TasksQueryParams = { sort, page, pageSize: 20 };
    if (tab !== 'all') base.status = tab.toUpperCase();
    if (tab === 'all' || tab === 'todo' || tab === 'doing') base.includeDone = 'false';
    if (q.trim()) base.q = q.trim();
    if (priority) base.priority = priority;
    if (areaId) base.areaId = areaId;
    if (projectId) base.projectId = projectId;
    if (tag) base.tag = tag;
    return base;
  }, [tab, q, priority, areaId, projectId, tag, sort, page]);

  const { data, isLoading, error } = useTasks(query);
  const today = todayStr();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">任务</h1>
        <Button onClick={() => setQuickOpen(true)}>
          <span className="flex items-center gap-1">
            <Icon name="plus" className="h-4 w-4" />
            添加任务
          </span>
        </Button>
      </div>

      {/* 筛选区 */}
      <Card className="space-y-3 p-4">
        <Tabs tabs={STATUS_TABS} active={tab} onChange={(t) => { setTab(t); setPage(1); }} />
        <div className="flex flex-wrap gap-2">
          <div className="relative min-w-48 flex-1">
            <Icon name="search" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="搜索任务标题/备注"
              className="pl-9"
            />
          </div>
          <Select value={priority} onChange={(e) => { setPriority(e.target.value); setPage(1); }} className="w-32">
            <option value="">全部优先级</option>
            {PRIORITY_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </Select>
          <Select value={areaId} onChange={(e) => { setAreaId(e.target.value); setPage(1); }} className="w-32">
            <option value="">全部领域</option>
            {(areas ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Select value={projectId} onChange={(e) => { setProjectId(e.target.value); setPage(1); }} className="w-32">
            <option value="">全部项目</option>
            {(projects?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <Select value={tag} onChange={(e) => { setTag(e.target.value); setPage(1); }} className="w-28">
            <option value="">全部标签</option>
            {(tags ?? []).map((t) => (
              <option key={t.id} value={t.name}>
                #{t.name}
              </option>
            ))}
          </Select>
          <Select value={sort} onChange={(e) => setSort(e.target.value as 'due')} className="w-28">
            <option value="due">按截止</option>
            <option value="priority">按优先级</option>
            <option value="created">按创建</option>
            <option value="updated">按更新</option>
          </Select>
        </div>
      </Card>

      {/* 列表 */}
      {error ? (
        <ErrorBox error={error} />
      ) : isLoading ? (
        <LoadingBlock text="加载任务…" />
      ) : (data?.items.length ?? 0) === 0 ? (
        <EmptyState
          title="没有符合条件的任务"
          description="调整筛选条件，或添加新任务"
          icon={<Icon name="tasks" className="mx-auto h-10 w-10" />}
          action={
            <Button size="sm" onClick={() => setQuickOpen(true)}>
              添加任务
            </Button>
          }
        />
      ) : (
        <ul className="space-y-2">
          {(data?.items ?? []).map((t) => {
            const overdue = t.dueDate && t.dueDate < today && (t.status === 'TODO' || t.status === 'DOING');
            return (
              <li key={t.id}>
                <Card className="flex flex-wrap items-center gap-3 p-3 transition-shadow hover:shadow-md">
                  {t.status === 'TODO' || t.status === 'DOING' ? (
                    <button
                      type="button"
                      onClick={() => completeTask.mutateAsync(t.id)}
                      className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-slate-300 text-white hover:border-emerald-500 hover:bg-emerald-500 dark:border-slate-600"
                      aria-label={`完成任务 ${t.title}`}
                    >
                      <Icon name="check" className="h-3 w-3" />
                    </button>
                  ) : (
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full ${
                        t.status === 'DONE'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-300 text-white dark:bg-slate-600'
                      }`}
                    >
                      <Icon name={t.status === 'DONE' ? 'check' : 'close'} className="h-3 w-3" />
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setDrawerId(t.id)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className={`truncate text-sm font-medium ${t.status === 'DONE' ? 'text-slate-400 line-through' : ''}`}>
                      {t.title}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                      {t.dueDate && (
                        <span className={`flex items-center gap-1 ${overdue ? 'text-rose-500' : ''}`}>
                          <Icon name="calendar" className="h-3 w-3" />
                          {relativeDay(t.dueDate)}
                          {t.dueTime ? ` ${t.dueTime}` : ''}
                        </span>
                      )}
                      {t.areaId && <span>· {(areas ?? []).find((a) => a.id === t.areaId)?.name ?? '领域'}</span>}
                      {t.projectId && <span>· {(projects?.items ?? []).find((p) => p.id === t.projectId)?.name ?? '项目'}</span>}
                      {t.tags.length > 0 && <span>· {t.tags.map((x) => `#${x.name}`).join(' ')}</span>}
                    </div>
                  </button>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${PRIORITY_STYLES[t.priority]}`}>
                    {PRIORITY_LABELS[t.priority]}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    {STATUS_LABELS[t.status]}
                  </span>
                  {(t.status === 'TODO' || t.status === 'DOING') && (
                    <button
                      type="button"
                      onClick={() => cancelTask.mutateAsync(t.id)}
                      className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600 dark:hover:bg-slate-800"
                      aria-label={`取消任务 ${t.title}`}
                    >
                      <Icon name="close" className="h-4 w-4" />
                    </button>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <Pagination page={data?.page ?? 1} totalPages={data?.totalPages ?? 1} onPage={setPage} />

      <QuickAdd open={quickOpen} onClose={() => setQuickOpen(false)} />
      <TaskDrawer taskId={drawerId} onClose={() => setDrawerId(null)} />
    </div>
  );
}
